import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { MdAccountBalance, MdArrowBack, MdAttachFile, MdBadge, MdBusiness, MdCheckCircle, MdPeople, MdStorefront, MdTune } from 'react-icons/md';
import MapPicker from '../MapPicker';
import { indianStates } from '../../Utils/states';
import { DOC_TYPES, FONT, GST_RX, IFSC_RX, PAN_RX, SellerAvatar } from '../../pages/Seller/sellerShared';

const Section = ({ title, hint, accent, icon: Icon, children }) => (
  <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, boxShadow: '0 4px 20px rgba(15,23,42,.04)' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
      <Box sx={{ background: `${accent}1a`, color: accent, borderRadius: '10px', p: 1, display: 'flex' }}><Icon size={18} /></Box>
      <Box>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 15, color: '#0f172a', lineHeight: 1.2 }}>{title}</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{hint}</Typography>
      </Box>
    </Box>
    {children}
  </Paper>
);

const SellerForm = ({
  sellerForm,
  handleFormChange,
  handleFileUpload,
  handleAddSeller,
  isEditing,
  setShowAddForm,
  hideTitle = false,
  hideBackButton = false,
  hideSubmitButton = false,
  sellerId = null,
}) => {
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [saving, setSaving] = useState(false);

  const validateField = (name, value) => {
    const v = String(value ?? '');
    switch (name) {
      case 'name': return !v.trim() ? 'Name is required' : '';
      case 'email': return !v.trim() ? 'Email is required' : !/^\S+@\S+\.\S+$/.test(v) ? 'Enter a valid email' : '';
      case 'password':
        if (!isEditing && !v) return 'Password is required';
        return v && v.length < 6 ? 'Use at least 6 characters' : '';
      case 'mobile': return !v.trim() ? 'Mobile number is required' : !/^\d{10}$/.test(v.replace(/\D/g, '')) ? 'Enter 10 digits' : '';
      case 'business_name': return !v.trim() ? 'Business name is required' : '';
      case 'businessAddress': return !v.trim() ? 'Business address is required' : '';
      case 'businessCity': return !v.trim() ? 'City is required' : '';
      case 'businessState': return !v.trim() ? 'State is required' : '';
      case 'businessPincode': return !v.trim() ? 'Pincode is required' : !/^\d{6}$/.test(v) ? 'Pincode must be 6 digits' : '';
      case 'gstNumber': return v.trim() && !GST_RX.test(v) ? 'That is not a valid 15-character GST number' : '';
      case 'businessPAN': return v.trim() && !PAN_RX.test(v) ? 'A PAN looks like ABCDE1234F' : '';
      case 'contactPerson2Email': return v.trim() && !/^\S+@\S+\.\S+$/.test(v) ? 'Enter a valid email' : '';
      case 'contactPerson2Mobile': return v.trim() && !/^\d{10}$/.test(v.replace(/\D/g, '')) ? 'Enter 10 digits' : '';
      case 'contactPerson2Password': return v && v.length < 6 ? 'Use at least 6 characters' : '';
      case 'commissionPercentage': return v !== '' && (isNaN(Number(v)) || Number(v) < 0 || Number(v) > 100) ? 'Enter a value from 0 to 100' : '';
      case 'bankIfsc': return v.trim() && !IFSC_RX.test(v) ? 'An IFSC looks like HDFC0001234' : '';
      case 'bankAccountNumber': return v.trim() && !/^\d{6,18}$/.test(v) ? 'Enter 6 to 18 digits' : '';
      default: return '';
    }
  };

  const handleInputChange = (e) => {
    let { name, value } = e.target;
    if (['gstNumber', 'businessPAN', 'bankIfsc'].includes(name)) {
      value = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      e.target.value = value;
    }
    handleFormChange(e);
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];
    if (!file) return;
    if (!file.type.startsWith('image/') && !file.type.includes('pdf')) return setErrors((p) => ({ ...p, [name]: 'Only images and PDF files are allowed' }));
    if (file.size > 5 * 1024 * 1024) return setErrors((p) => ({ ...p, [name]: 'The file must be under 5 MB' }));
    setErrors((p) => ({ ...p, [name]: undefined, documents: undefined }));
    handleFileUpload(e);
  };

  const validateForm = () => {
    const next = {};
    ['name', 'email', 'mobile', 'business_name', 'businessAddress', 'businessCity', 'businessState', 'businessPincode', 'gstNumber', 'businessPAN',
      'contactPerson2Email', 'contactPerson2Mobile', 'commissionPercentage', 'bankIfsc', 'bankAccountNumber'].forEach((f) => {
      const err = validateField(f, sellerForm[f]);
      if (err) next[f] = err;
    });
    if (!isEditing && !sellerForm.password) next.password = 'Password is required';
    else if (sellerForm.password && sellerForm.password.length < 6) next.password = 'Use at least 6 characters';
    if (sellerForm.contactPerson2Name) {
      if (!sellerForm.contactPerson2Email) next.contactPerson2Email = 'Email is needed for their login';
      if (!sellerForm.contactPerson2Mobile) next.contactPerson2Mobile = 'Mobile is needed for their login';
      if (!isEditing && !sellerForm.contactPerson2Password) next.contactPerson2Password = 'Set a password for their login';
    }
    if (!sellerForm.storeLatitude || !sellerForm.storeLongitude) next.storeLatitude = 'Pick the store location on the map';
    if (!isEditing && !DOC_TYPES.some((d) => !!sellerForm[d.id])) next.documents = 'Upload at least one document';
    setErrors(next);
    return next;
  };

  const onSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setSubmitError('');
    const found = validateForm();
    if (Object.keys(found).length) {
      setSubmitError('Please fix the highlighted fields.');
      // jump to the first section that has a problem
      const order = ['business_name', 'name', 'commissionPercentage', 'documents', 'storeLatitude'];
      const first = Object.keys(found)[0];
      const el = document.getElementById(`field-${first}`) || document.getElementById(`field-${order.find((o) => found[o])}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSaving(true);
    try {
      await handleAddSeller();
    } finally {
      setSaving(false);
    }
  };

  const fetchAddressFromLatLng = async (lat, lng) => {
    try {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`);
      const data = await response.json();
      if (data.status !== 'OK') return { storeLatitude: lat, storeLongitude: lng };
      const result = data.results[0];
      const get = (type) => (result.address_components.find((c) => c.types.includes(type)) || {}).long_name || '';
      return {
        storeAddress: result.formatted_address || '',
        storeArea: get('sublocality_level_1') || get('locality') || '',
        storeNearbyLandmark: get('neighborhood') || '',
        storeCity: get('locality') || get('administrative_area_level_2') || '',
        storeState: get('administrative_area_level_1') || '',
        storePincode: get('postal_code') || '',
        storeLatitude: lat,
        storeLongitude: lng,
      };
    } catch (error) {
      return { storeLatitude: lat, storeLongitude: lng };
    }
  };

  const text = (name, label, extra = {}) => (
    <TextField
      id={`field-${name}`}
      fullWidth
      size="small"
      label={label}
      name={name}
      value={sellerForm[name] ?? ''}
      onChange={handleInputChange}
      error={!!errors[name]}
      helperText={errors[name] || extra.helper}
      {...extra.props}
    />
  );

  const docCount = DOC_TYPES.filter((d) => !!sellerForm[d.id]).length;

  return (
    <Box sx={{ p: hideTitle ? 0 : { xs: 1.5, md: 3 }, background: hideTitle ? 'transparent' : '#f8fafc', minHeight: hideTitle ? 'auto' : '100vh' }}>
      <Box sx={{ maxWidth: 1150, mx: 'auto' }}>
        {sellerId && (
          <Alert severity="info" sx={{ mb: 2 }}>Registration link ID: {sellerId}</Alert>
        )}
        {!hideTitle && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            {!hideBackButton && (
              <IconButton onClick={() => setShowAddForm(false)} aria-label="back" sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}><MdArrowBack size={20} /></IconButton>
            )}
            <Box>
              <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a', lineHeight: 1.2 }}>{isEditing ? 'Edit Seller' : 'Add Seller'}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{isEditing ? sellerForm.business_name || sellerForm.name : 'Register a seller who will list and fulfil products.'}</Typography>
            </Box>
          </Box>
        )}

        <Box component="form" onSubmit={onSubmit} noValidate>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Section title="Business" hint="Registered business details" accent="#6366f1" icon={MdBusiness}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>{text('business_name', 'Business name', { props: { required: true } })}</Grid>
                    <Grid item xs={12} sm={6}>{text('sellerLicenseNumber', 'Seller license number', { helper: 'Optional' })}</Grid>
                    <Grid item xs={12} sm={6}>{text('gstNumber', 'GST number', { helper: 'Optional · 15 characters', props: { inputProps: { maxLength: 15, style: { fontFamily: 'monospace' } } } })}</Grid>
                    <Grid item xs={12} sm={6}>{text('businessPAN', 'Business PAN', { helper: 'Optional', props: { inputProps: { maxLength: 10, style: { fontFamily: 'monospace' } } } })}</Grid>
                    <Grid item xs={12}>{text('businessAddress', 'Business address', { props: { required: true, multiline: true, rows: 2 } })}</Grid>
                    <Grid item xs={12} sm={4}>{text('businessCity', 'City', { props: { required: true } })}</Grid>
                    <Grid item xs={12} sm={4}>
                      {text('businessState', 'State', { props: { required: true, select: true, children: indianStates.map((st) => <MenuItem key={st} value={st}>{st}</MenuItem>) } })}
                    </Grid>
                    <Grid item xs={12} sm={4}>{text('businessPincode', 'Pincode', { props: { required: true, inputProps: { maxLength: 6 } } })}</Grid>
                  </Grid>
                </Section>

                <Section title="Contact persons" hint="The primary contact signs in with these details" accent="#ec4899" icon={MdPeople}>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, mb: 1.5 }}>Primary contact</Typography>
                  <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>{text('name', 'Full name', { props: { required: true } })}</Grid>
                    <Grid item xs={12} sm={6}>{text('mobile', 'Mobile', { props: { required: true, inputProps: { maxLength: 12 } }, helper: '10 digits' })}</Grid>
                    <Grid item xs={12} sm={6}>{text('email', 'Email (login)', { props: { required: true, type: 'email' } })}</Grid>
                    <Grid item xs={12} sm={6}>{text('password', isEditing ? 'New password' : 'Password', { props: { type: 'password', required: !isEditing, autoComplete: 'new-password' }, helper: isEditing ? 'Leave empty to keep the current one' : 'At least 6 characters' })}</Grid>
                  </Grid>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, mb: 0.25 }}>Secondary contact <Chip size="small" label="optional" sx={{ ml: 0.5, height: 18, fontSize: 10.5 }} /></Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b', mb: 1.5 }}>A second person who can also sign in to the seller panel.</Typography>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>{text('contactPerson2Name', 'Full name')}</Grid>
                    <Grid item xs={12} sm={6}>{text('contactPerson2Mobile', 'Mobile', { props: { inputProps: { maxLength: 12 } } })}</Grid>
                    <Grid item xs={12} sm={6}>{text('contactPerson2Email', 'Email (login)', { props: { type: 'email' } })}</Grid>
                    <Grid item xs={12} sm={6}>{text('contactPerson2Password', isEditing ? 'New password' : 'Password', { props: { type: 'password', autoComplete: 'new-password' }, helper: isEditing ? 'Leave empty to keep the current one' : '' })}</Grid>
                  </Grid>
                </Section>

                <Section title="Documents" hint="Images or PDF, up to 5 MB each" accent="#0ea5e9" icon={MdBadge}>
                  {errors.documents && <Alert severity="error" sx={{ mb: 2 }} id="field-documents">{errors.documents}</Alert>}
                  <Grid container spacing={1.5}>
                    {DOC_TYPES.map((doc) => {
                      const value = sellerForm[doc.id];
                      const isNew = value && typeof value !== 'string';
                      const has = !!value;
                      return (
                        <Grid item xs={12} sm={6} key={doc.id}>
                          <Box sx={{ p: 1.5, borderRadius: '12px', border: `1.5px solid ${errors[doc.id] ? '#ef4444' : has ? '#86efac' : '#e2e8f0'}`, background: has ? '#f0fdf4' : '#fff', display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <Box sx={{ color: has ? '#16a34a' : '#94a3b8', display: 'flex' }}>{has ? <MdCheckCircle size={22} /> : <MdAttachFile size={22} />}</Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 13 }}>{doc.label}</Typography>
                              <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: errors[doc.id] ? '#dc2626' : '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {errors[doc.id] || (isNew ? value.name : has ? 'Uploaded' : 'Not uploaded')}
                              </Typography>
                            </Box>
                            {has && !isNew && <Button size="small" href={value} target="_blank" rel="noopener noreferrer" sx={{ textTransform: 'none', fontWeight: 700 }}>View</Button>}
                            <Button component="label" size="small" sx={{ textTransform: 'none', fontWeight: 800, background: '#f1f5f9', color: '#0f172a', borderRadius: '8px', px: 1.5, '&:hover': { background: '#e2e8f0' } }}>
                              {has ? 'Replace' : 'Upload'}
                              <input type="file" hidden name={doc.id} accept="image/*,application/pdf" onChange={handleFileChange} />
                            </Button>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Section>

                <Section title="Store" hint="Where products are packed and picked up" accent="#10b981" icon={MdStorefront}>
                  <Box id="field-storeLatitude">
                    {errors.storeLatitude && <Alert severity="error" sx={{ mb: 2 }}>{errors.storeLatitude}</Alert>}
                    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b', mb: 1 }}>Click on the map to set the store location; the address fills in for you.</Typography>
                    <MapPicker
                      lat={sellerForm.storeLatitude}
                      lng={sellerForm.storeLongitude}
                      onChange={async ({ lat, lng }) => {
                        const addr = await fetchAddressFromLatLng(lat, lng);
                        Object.entries(addr).forEach(([name, value]) => handleFormChange({ target: { name, value } }));
                        setErrors((prev) => ({ ...prev, storeLatitude: undefined }));
                      }}
                    />
                  </Box>
                  <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Store address" name="storeAddress" multiline rows={2} value={sellerForm.storeAddress || ''} onChange={handleFormChange} />
                    </Grid>
                    <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Area" name="storeArea" value={sellerForm.storeArea || ''} onChange={handleFormChange} /></Grid>
                    <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Nearby landmark" name="storeNearbyLandmark" value={sellerForm.storeNearbyLandmark || ''} onChange={handleFormChange} /></Grid>
                    <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="City" name="storeCity" value={sellerForm.storeCity || ''} onChange={handleFormChange} /></Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField select fullWidth size="small" label="State" name="storeState" value={sellerForm.storeState || ''} onChange={handleFormChange}>
                        {indianStates.map((st) => <MenuItem key={st} value={st}>{st}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Pincode" name="storePincode" value={sellerForm.storePincode || ''} onChange={handleFormChange} inputProps={{ maxLength: 6 }} /></Grid>
                    <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="Latitude" value={sellerForm.storeLatitude || ''} InputProps={{ readOnly: true }} /></Grid>
                    <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="Longitude" value={sellerForm.storeLongitude || ''} InputProps={{ readOnly: true }} /></Grid>
                    <Grid item xs={12}><TextField fullWidth size="small" label="About the store" name="storeDescription" multiline rows={2} value={sellerForm.storeDescription || ''} onChange={handleFormChange} helperText="Optional" /></Grid>
                  </Grid>
                </Section>

                <Section title="Commission & payout" hint="How much the platform keeps, and where payouts go" accent="#f59e0b" icon={MdAccountBalance}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={4}>{text('commissionPercentage', 'Commission (%)', { props: { type: 'number', inputProps: { min: 0, max: 100, step: 0.5 } }, helper: 'Taken from each sale. Default 10' })}</Grid>
                    <Grid item xs={12} sm={4}>{text('bankAccountHolder', 'Account holder name')}</Grid>
                    <Grid item xs={12} sm={4}>{text('bankName', 'Bank name')}</Grid>
                    <Grid item xs={12} sm={4}>{text('bankAccountNumber', 'Account number', { props: { inputProps: { inputMode: 'numeric' } } })}</Grid>
                    <Grid item xs={12} sm={4}>{text('bankIfsc', 'IFSC', { props: { inputProps: { maxLength: 11, style: { fontFamily: 'monospace' } } } })}</Grid>
                    <Grid item xs={12} sm={4}>{text('bankUpiId', 'UPI ID', { helper: 'Optional' })}</Grid>
                  </Grid>
                </Section>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ position: { md: 'sticky' }, top: 16, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                  <Box sx={{ display: 'inline-block', mb: 1 }}><SellerAvatar seller={{ name: sellerForm.name, business_name: sellerForm.business_name }} size={64} /></Box>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 16 }}>{sellerForm.business_name || 'New seller'}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>{sellerForm.name || 'Contact person'}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center', flexWrap: 'wrap', mt: 1.25 }}>
                    <Chip size="small" label={`${docCount}/${DOC_TYPES.length} documents`} sx={{ fontFamily: FONT, fontWeight: 700, background: docCount ? '#dcfce7' : '#f1f5f9', color: docCount ? '#166534' : '#64748b' }} />
                    <Chip size="small" label={sellerForm.storeLatitude ? 'Location set' : 'No location'} sx={{ fontFamily: FONT, fontWeight: 700, background: sellerForm.storeLatitude ? '#dcfce7' : '#fef3c7', color: sellerForm.storeLatitude ? '#166534' : '#92400e' }} />
                  </Box>
                </Paper>

                <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}><MdTune color="#64748b" /><Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14 }}>Approval status</Typography></Box>
                  <TextField select fullWidth size="small" name="status" value={sellerForm.status || 'pending'} onChange={handleFormChange} helperText="Only approved sellers can sell">
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="waitingForApproval">Waiting for approval</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </TextField>
                </Paper>

                {submitError && <Alert severity="error">{submitError}</Alert>}

                {!hideSubmitButton && (
                  <Button type="submit" variant="contained" disabled={saving} sx={{ fontFamily: FONT, textTransform: 'none', background: 'rgb(255 209 2)', color: 'black', py: 1.2, borderRadius: '12px', fontWeight: 800, fontSize: 14, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } }}>
                    {saving ? 'Saving…' : isEditing ? 'Update seller' : 'Add seller'}
                  </Button>
                )}
                {!hideBackButton && <Button onClick={() => setShowAddForm(false)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default SellerForm;
